import { useState } from 'react'
import { OPERACOES_ANUNCIO, type ContaAnuncioResumo } from '@baiocchi/shared'
import {
  Alerta,
  Botao,
  CabecalhoCartao,
  Cartao,
  Campo,
  Entrada,
  Esqueleto,
  EstadoVazio,
  Etiqueta,
  Modal,
  Selecao,
  useAvisos,
} from '@baiocchi/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { chaves } from '../../api/chaves'
import { metaAdsApi } from '../../api/meta-ads'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { IconeAtualizar } from '../../componentes/icones'
import { CabecalhoPagina } from '../../layout/layout-app'
import { formatarTempoRelativo } from '../../utilitarios/formatar'
import { rotularOperacao } from './filtros'

/**
 * TELA-META-02 — Contas de anúncio (§21).
 *
 * ## Conta de anúncio é cadastro, não variável de ambiente
 *
 * A alternativa era uma variável por conta (`META_ADS_CONTA_VENDA`, `..._ALUGUEL`).
 * Descartada porque cadastrar a terceira conta exigiria deploy, "todas as contas" não seria
 * expressável como consulta, e a operação (venda/aluguel) é decisão de negócio que muda por
 * conversa, não por release.
 *
 * ## O ID é conferido contra a Meta no cadastro
 *
 * São 15 dígitos digitados à mão — errar é o caso mais provável. Se a validação só
 * acontecesse na primeira sincronização, o gestor veria "salvo com sucesso" e descobriria o
 * erro seis horas depois, num campo de erro que ele não sabe que existe.
 *
 * ## `ultimoErro` é exibido, não escondido
 *
 * É a única tela que responde "por que os números não atualizaram?". Guardar a mensagem da
 * Meta num log a que o gestor não tem acesso transformaria um problema de credencial em um
 * mistério.
 */
export function PaginaMetaAdsContas() {
  const cliente = useQueryClient()
  const avisos = useAvisos()
  const [criando, setCriando] = useState(false)

  const consulta = useQuery({
    queryKey: chaves.metaAds.contas,
    queryFn: ({ signal }) => metaAdsApi.contas(signal),
  })

  const sincronizar = useMutation({
    mutationFn: (contaId?: number) =>
      metaAdsApi.sincronizar(contaId === undefined ? undefined : { contaId: String(contaId) }),
    onSuccess: () => {
      avisos.mostrar({
        tom: 'sucesso',
        titulo: 'Sincronização concluída',
        descricao: 'Estrutura e métricas atualizadas. Confira o painel.',
      })
      void cliente.invalidateQueries({ queryKey: chaves.metaAds.todos })
    },
    onError: () => {
      avisos.mostrar({
        tom: 'erro',
        titulo: 'A sincronização falhou',
        descricao: 'Veja o erro registrado na conta abaixo.',
      })
      void cliente.invalidateQueries({ queryKey: chaves.metaAds.contas })
    },
  })

  const contas = consulta.data?.dados ?? []

  return (
    <>
      <CabecalhoPagina
        titulo="Contas de anúncio"
        descricao="Quais contas o Command Center acompanha, e a que operação cada uma atende."
        acoes={
          <>
            <Botao
              variante="contorno"
              tamanho="sm"
              onClick={() => sincronizar.mutate(undefined)}
              carregando={sincronizar.isPending}
              iconeEsquerda={<IconeAtualizar className="h-4 w-4" />}
            >
              Sincronizar agora
            </Botao>
            <Botao tamanho="sm" onClick={() => setCriando(true)}>
              Cadastrar conta
            </Botao>
          </>
        }
      />

      {consulta.isError ? (
        <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
      ) : null}

      {consulta.isLoading ? <Esqueleto className="h-40 w-full" /> : null}

      {consulta.isSuccess && contas.length === 0 ? (
        <Cartao>
          <EstadoVazio
            titulo="Nenhuma conta cadastrada"
            descricao="Cadastre as contas de anúncio da Baiocchi para o Command Center começar a sincronizar métricas."
            acao={<Botao onClick={() => setCriando(true)}>Cadastrar a primeira conta</Botao>}
          />
        </Cartao>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        {contas.map((conta) => (
          <CartaoConta
            key={conta.id}
            conta={conta}
            sincronizando={sincronizar.isPending && sincronizar.variables === conta.id}
            aoSincronizar={() => sincronizar.mutate(conta.id)}
          />
        ))}
      </div>

      {criando ? (
        <ModalCadastro
          aoFechar={() => setCriando(false)}
          aoSalvar={() => {
            setCriando(false)
            void cliente.invalidateQueries({ queryKey: chaves.metaAds.contas })
          }}
        />
      ) : null}
    </>
  )
}

function CartaoConta({
  conta,
  sincronizando,
  aoSincronizar,
}: {
  readonly conta: ContaAnuncioResumo
  readonly sincronizando: boolean
  readonly aoSincronizar: () => void
}) {
  return (
    <Cartao semPadding>
      <CabecalhoCartao
        titulo={
          <span className="flex flex-wrap items-center gap-2">
            {conta.nome}
            <Etiqueta tom={conta.operacao === 'VENDA' ? 'marca' : 'info'} tamanho="sm">
              {rotularOperacao(conta.operacao)}
            </Etiqueta>
            {!conta.ativo ? (
              <Etiqueta tom="neutro" tamanho="sm">
                Inativa
              </Etiqueta>
            ) : null}
          </span>
        }
        descricao={
          <span className="font-mono text-xs">{conta.contaExternaIdMascarado}</span>
        }
        acoes={
          <Botao
            variante="fantasma"
            tamanho="sm"
            onClick={aoSincronizar}
            carregando={sincronizando}
          >
            Sincronizar
          </Botao>
        }
      />

      <div className="space-y-2 p-4 sm:p-5">
        <dl className="space-y-1 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-conteudo-suave">Última sincronização</dt>
            <dd>
              {conta.ultimaSincronizacaoEm === null ? (
                <span className="text-atencao">nunca</span>
              ) : (
                formatarTempoRelativo(conta.ultimaSincronizacaoEm)
              )}
            </dd>
          </div>
          {conta.moeda !== null ? (
            <div className="flex justify-between gap-3">
              <dt className="text-conteudo-suave">Moeda</dt>
              <dd>{conta.moeda}</dd>
            </div>
          ) : null}
          {conta.cplAlvo !== null ? (
            <div className="flex justify-between gap-3">
              <dt className="text-conteudo-suave">CPL alvo</dt>
              <dd className="tabular-nums">
                {conta.cplAlvo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </dd>
            </div>
          ) : null}
        </dl>

        {/*
          O erro da última sincronização, com a mensagem da Meta. É a única informação que
          responde "por que os números não atualizaram?" — e ela pertence a quem opera, não
          ao log do servidor.
        */}
        {conta.ultimoErro !== null ? (
          <Alerta tom="perigo">
            <span className="block text-xs font-medium">Erro na última sincronização</span>
            <span className="mt-1 block break-words text-xs">{conta.ultimoErro}</span>
          </Alerta>
        ) : null}

        {conta.ultimaSincronizacaoEm === null && conta.ultimoErro === null ? (
          <Alerta tom="info">
            Esta conta ainda não sincronizou. Use &ldquo;Sincronizar&rdquo; acima, ou espere o
            job automático — a estrutura roda a cada 6 h e as métricas a cada hora.
          </Alerta>
        ) : null}
      </div>
    </Cartao>
  )
}

function ModalCadastro({
  aoFechar,
  aoSalvar,
}: {
  readonly aoFechar: () => void
  readonly aoSalvar: () => void
}) {
  const avisos = useAvisos()
  const [contaExternaId, setContaExternaId] = useState('')
  const [nome, setNome] = useState('')
  const [operacao, setOperacao] = useState<ContaAnuncioResumo['operacao']>('VENDA')
  const [cplAlvo, setCplAlvo] = useState('')

  const criar = useMutation({
    mutationFn: () =>
      metaAdsApi.criarConta({
        contaExternaId: contaExternaId.trim(),
        nome: nome.trim(),
        operacao,
        ativo: true,
        cplAlvo: cplAlvo.trim() === '' ? null : Number.parseFloat(cplAlvo.replace(',', '.')),
      }),
    onSuccess: () => {
      avisos.mostrar({ tom: 'sucesso', titulo: 'Conta cadastrada' })
      aoSalvar()
    },
    onError: (erro: unknown) => {
      avisos.mostrar({
        tom: 'erro',
        titulo: 'Não foi possível cadastrar',
        descricao: erro instanceof Error ? erro.message : undefined,
      })
    },
  })

  return (
    <Modal
      aberto
      onFechar={aoFechar}
      titulo="Cadastrar conta de anúncio"
      largura="md"
      travado={criar.isPending}
      rodape={
        <>
          <Botao variante="contorno" onClick={aoFechar} disabled={criar.isPending}>
            Cancelar
          </Botao>
          <Botao
            onClick={() => criar.mutate()}
            carregando={criar.isPending}
            disabled={contaExternaId.trim().length === 0 || nome.trim().length < 2}
          >
            Cadastrar
          </Botao>
        </>
      }
    >
      <div className="space-y-3">
        <Campo
          rotulo="ID da conta de anúncio"
          dica="Encontre no Gerenciador de Anúncios. Aceita act_123456789 ou apenas os números."
        >
          {(atributos) => (
            <Entrada
              {...atributos}
              value={contaExternaId}
              onChange={(evento) => setContaExternaId(evento.target.value)}
              placeholder="act_123456789012345"
              autoComplete="off"
            />
          )}
        </Campo>

        <Campo rotulo="Nome" dica="Como a conta aparece nos filtros e nos relatórios.">
          {(atributos) => (
            <Entrada
              {...atributos}
              value={nome}
              onChange={(evento) => setNome(evento.target.value)}
              placeholder="CA - Baiocchi Imóveis"
              maxLength={160}
            />
          )}
        </Campo>

        <Campo
          rotulo="Operação"
          dica="Venda e aluguel têm CPL e ciclo incomparáveis — a separação é o que permite a comparação lado a lado."
        >
          {(atributos) => (
            <Selecao
              {...atributos}
              value={operacao}
              onChange={(evento) =>
                setOperacao(evento.target.value as ContaAnuncioResumo['operacao'])
              }
              opcoes={OPERACOES_ANUNCIO.map((valor) => ({
                valor,
                rotulo: rotularOperacao(valor),
              }))}
            />
          )}
        </Campo>

        <Campo
          rotulo="CPL alvo (opcional)"
          dica="Custo por lead que a operação considera aceitável. Usado como referência de negócio no painel."
        >
          {(atributos) => (
            <Entrada
              {...atributos}
              value={cplAlvo}
              onChange={(evento) => setCplAlvo(evento.target.value)}
              placeholder="25,00"
              inputMode="decimal"
            />
          )}
        </Campo>

        <Alerta tom="info">
          O ID é conferido na Meta ao salvar. Se o token não tiver acesso à conta ou o ID
          estiver errado, o cadastro é recusado com a explicação — em vez de falhar em
          silêncio na primeira sincronização.
        </Alerta>
      </div>
    </Modal>
  )
}
